import React, { useEffect, useState } from 'react';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import queryString from 'query-string';
import moment from 'moment/moment';
import PropTypes from 'prop-types';

import { duckify, filterDuck, singleDuck } from '../../utils/duckify';
import {
  modals,
  schoolYears,
  fields as fieldsDuck,
  users as usersDuck,
  billingCategoryGroups as billingCategories,
  grades, customers as customersDuck,
  contractFees, flashMessages, chargeRules
} from '../../ducks';
import withPagination from '../../hocs/withPagination';
import { apiFetch, formatCurrency, titleize } from '../../utils';
import Filters from '../Filters/Filters';

import '../billing/styles/LedgerItemList.scss';

import withFiltering from '../../hocs/withFiltering';
import ContactList from '../ContactList/ContactList';
import Placeholder from '../Placeholder';
import withDateFilter from '../../hocs/withDateFilter';
import {
  currencyColumn,
  dateColumn,
  defaultColumn, expanderColumn
} from '../ContactList/ColumnDefinitions';
import withSchoolYear from '../../hocs/withSchoolYear';
import withSorting from '../../hocs/withSorting';
import { CONTRACT_CHARGE_TYPES } from '../fields/ChargeTypes';
import FlashRoot from '../FlashMessage/FlashRoot';
import SchoolYearSelect from '../SchoolYearSelect';
import ModalRoot from '../ModalRoot';
import ContractFeeModal from './ContractFeeModal';
import ContractRuleModal from './ContractRuleModal';
import './styles/ContractFeesList.scss';
import CustomerNote from './CustomerNote';
import { TimeframeFetcher } from '../../utils/timeFrameFetcher';

const ContractFeesList = ({
  billingCategories,
  customer,
  dispatch,
  filters,
  additionalFields,
  allFields,
  page,
  schoolYears,
  setFilters,
  setPage,
  onSchoolYearChange,
  schoolYearId,
  contractFees,
  onSort,
  sort,
  showFlash,
  activateContractFee,
  deactivateContractFee,
  reloadContractFees,
  removeContractRule,
  currentUser,
  timeframeFetcher
}) => {
  const { total, perPage } = contractFees;
  const totalPages = Math.ceil(total / perPage);
  const paymentMethods = Object.values(customer.payment_config.payment_methods)
    .map(m => [m.display_name, m.canonical_name]);
  paymentMethods.sort();
  const isSuperAdmin = currentUser.is_super_admin; const isArchived = year => year.archived
  const isSelected = year => year.id == schoolYearId
  const dropdownSchoolYears = schoolYears.filter(year => !isArchived(year) || isSelected(year))

  const showEditHistory = (resourceName, id) => {
    dispatch(modals.actions.show('AuditViewer', { id, resource: resourceName }));
  };

  const processActivateContractFee = (contractFee) => {
    activateContractFee(contractFee.id).then(() => {
      showFlash({ message: `Contract Fee "${contractFee.name}" has been activated.` });
      reloadContractFees();
    }).catch((response) => {
      if (response.error) { showFlash({ type: 'danger', message: response.error }); }
    });
  };

  const processDeactivateContractFee = (contractFee) => {
    deactivateContractFee(contractFee.id).then(() => {
      showFlash({ message: `Contract Fee "${contractFee.name}" has been deactivated.` });
      reloadContractFees();
    }).catch((response) => {
      if (response.error) { showFlash({ type: 'danger', message: response.error }); }
    });
  };

  const processDeleteContractRule = (contractRule) => removeContractRule(contractRule).then(() => {
    showFlash({ message: `Contract Rule "${contractRule.description}" has been deleted.` });
    reloadContractFees();
  }).catch((response) => {
    if (response.error) { showFlash({ type: 'danger', message: response.error }); }
  });

  const removeContractRuleWithConfirmation = (contractRule) => {
    dispatch(modals.actions.show('VerificationModal', {
      title: 'Delete Contract Rule',
      message: (<div>
        <h2>
          Are you sure you want to delete Contract Rule "{contractRule.description}"?
        </h2>
      </div>),
      onClose: () => {
        dispatch(modals.actions.hide());
      },
      onConfirm: () => processDeleteContractRule(contractRule)
    }));
  };

  const activateContractFeeWithConfirmation = (contractFee) => {
    const action = contractFee.active ? 'deactivate' : 'activate';

    dispatch(modals.actions.show('VerificationModal', {
      title: `${titleize(action)} Contract Rule`,
      message: (<div><h2>Are you sure you want to {action} Contract Fee "{contractFee.name}"?</h2></div>),
      onClose: () => {
        dispatch(modals.actions.hide());
      },
      onConfirm: () => (contractFee.active ? processDeactivateContractFee(contractFee) : processActivateContractFee(contractFee))
    }));
  };

  const showContractFeeModal = (contractFee) => {
    dispatch(modals.actions.show('ContractFeeModal', {
      contractFee,
      billingCategories
    }));
  };

  const showContractRuleModal = (contractRule) => {
    const selectedContractFee = contractFees.find((contractFee) => contractFee.id === contractRule.contract_fee_id);
    dispatch(modals.actions.show('ContractRuleModal', {
      contractRule,
      billingCategories,
      contractFee: selectedContractFee
    }));
  };

  // options include the group (charge) category and each child (credit) category
  const billingCategoryOptions = billingCategories.map((group) => (
    [
      [
        `${group.name}${group.active ? '' : ' (inactive)'}`,
        group.id
      ]
    ].concat(
      group.children.map((category) => (
        [
          `${category.name}${category.active ? '' : ' (inactive)'}`,
          category.id
        ]
      ))
    )
  )).flat();

  const schoolYearItems = schoolYears.map(y => [y.name, y.id]);
  schoolYearItems.push(['no term', 'no-term']);

  const filterFields = [
    {
      solr_prompt: 'billing_category_id',
      display_name: 'Billing Category',
      options: billingCategoryOptions
    },
    {
      solr_prompt: 'charge_type',
      display_name: 'Charge Type',
      options: Object.values(CONTRACT_CHARGE_TYPES).map((value) => [value, value])
    },
    {
      solr_prompt: 'active',
      display_name: 'Is Active',
      options: [['Active', 'true'], ['Inactive', 'false']]
    },
    {
      filter_type: 'TextFilter',
      solr_prompt: 'contract_fee_name',
      display_name: 'Contract Fee Name'
    }
  ];

  const displayAmount = (contractFee) => {
    const field = allFields.find(amountField => amountField.id === contractFee.field_id);

    if (contractFee.amount_type === 'percent') {
      return `${contractFee.amount}% of ...`;
    } else if (contractFee.amount_type === 'field' && field) {
      return field.display_name;
    }

    return formatCurrency(contractFee.amount);
  };

  const [isLocked, setIsLocked] = useState(false);
  useEffect(() => {
    if (timeframeFetcher && schoolYearId) {
      timeframeFetcher.fetchTimeframe('enrollment', schoolYearId)
        .then((timeframe) => {
          setIsLocked(!!timeframe);
        });
    }
  }, [timeframeFetcher, schoolYearId]);

  useEffect(() => {
    if (contractFees.error) {
      setIsLocked(true);
    }
  }, [contractFees]);

  /** The list of columns to show for this page */
  const columns = [
    expanderColumn({}),
    defaultColumn({
      header: 'Name',
      sortName: 'contract_fees.name',
      value: (item) => (
        <td>
          {titleize(item.name)}
        </td>
      )
    }),
    defaultColumn({
      header: 'Canonical Name',
      sortName: 'contract_fees.canonical_name',
      value: 'canonical_name'
    }),
    currencyColumn({
      header: 'Amount',
      defaultToZero: true,
      sortName: 'contract_fees.amount',
      columnClassNames: 'amount_column',
      value: (item) => (
        <td>
          {displayAmount(item)}
        </td>
      )
    }),
    defaultColumn({
      header: 'Billing Category',
      sortName: 'billing_categories.name',
      value: 'billing_category_name'
    }),
    defaultColumn({
      header: 'Charge Type',
      sortName: 'contract_fees.charge_type',
      value: 'charge_type_name'
    }),
    defaultColumn({
      header: 'Add To Deposit',
      sortName: 'contract_fees.add_to_deposit',
      value: (item) => (
        <td>
          <input disabled type='checkbox' checked={item.add_to_deposit} />
        </td>
      )
    }),
    defaultColumn({
      header: 'Active',
      sortName: 'contract_fees.active',
      value: (item) => (
        <td>
          <input disabled type='checkbox' checked={item.active} />
        </td>
      )
    }),
    dateColumn({
      header: 'Last Edit',
      sortName: 'contract_fees.updated_at',
      columnClassNames: 'last_edit_column',
      value: (item) => (
        <td>
          {moment(item.last_edit).format('YYYY-MM-DD')}
        </td>
      )
    }),
    defaultColumn({
      header: 'Edit History',
      columnClassNames: 'edit_history_column',
      value: (item) => (
        <td>
          <span className='view_changes_button' onClick={() => showEditHistory('contract_fees', item.id)}>
                View Changes
          </span>
        </td>
      )
    }),
    defaultColumn({
      header: '',
      value: (item) => (
        <td>
          <ul className='action-list'>
            {billingCategories.ready && <li>
              <span className={`edit_contract_fee lockable ${isLocked ? 'locked' : ''}`} onClick={() => {
                if (!isLocked) {
                  showContractFeeModal(item);
                }
              }}>
                Edit
              </span>
            </li>}
            {isSuperAdmin && (
              <li>
                <span className={`add_rule lockable ${isLocked ? 'locked' : ''}`} onClick={() => {
                  if (!isLocked) {
                    showContractRuleModal({
                      contract_fee_id: item.id,
                      school_year_id: schoolYearId
                    });
                  }
                }}>
                  Add Rule
                </span>
              </li>
            )}
            <li className={`lockable ${isLocked ? 'locked' : ''} danger-link`}>
              {
                item.active
                  ? (
                    <span className='deactivate_contract_fee' onClick={() => {
                      if (!isLocked) {
                        activateContractFeeWithConfirmation(item);
                      }
                    }}>
                      Make Inactive
                    </span>
                  )
                  : (
                    <span className='activate_contract_fee' onClick={() => {
                      if (!isLocked) {
                        activateContractFeeWithConfirmation(item);
                      }
                    }}>
                      Make Active
                    </span>
                  )
              }
            </li>
          </ul>
        </td>
      )
    })
  ];

  const contractRulesColumns = [
    defaultColumn({
      header: 'Rule Description',
      value: 'description'
    }),
    defaultColumn({
      header: 'Rule',
      value: 'test'
    }),
    defaultColumn({
      header: 'Active',
      value: (item) => (
        <td>
          <input disabled type='checkbox' checked={item.active} />
        </td>
      )
    }),
    dateColumn({
      header: 'Last Edit',
      columnClassNames: 'last_edit_column',
      value: (item) => (
        <td>
          {moment(item.last_edit).format('YYYY-MM-DD')}
        </td>
      )
    }),
    defaultColumn({
      header: 'Edit History',
      columnClassNames: 'edit_history_column',
      value: (item) => (
        <td>
          <span className='view_changes_button' onClick={() => showEditHistory('charge_rules', item.id)}>
            View Changes
          </span>
        </td>
      )
    }),
    defaultColumn({
      header: '',
      value: (item) => (
        <td>
          {isSuperAdmin && (<ul className='action-list'>
            <li className={`lockable ${isLocked ? 'locked' : ''}`}>
              <span className='edit_contract_rule' onClick={() => {
                if (!isLocked) {
                  showContractRuleModal(item);
                }
              }}>
                Edit
              </span>
            </li>
            <li className={`lockable ${isLocked ? 'locked' : ''}`}>
            <span className='delete_contract_rule' onClick={() => {
              if (!isLocked) {
                removeContractRuleWithConfirmation(item);
              }
            }}>
              Delete
            </span>
            </li>
          </ul>)}
        </td>

      )
    })
  ];

  const makeContractRulesList = (contractFeeItems) => (
    <ContactList
      className='contact-rule-table'
      columns={contractRulesColumns}
      contacts={contractFeeItems}
      emptyMessage='There are no contract rules.'
      onSort={onSort}
      sort={sort}
    />
  );

  return (
    <div key={JSON.stringify({
      filters, schoolYears, billingCategories, page
    })}
    >
      <FlashRoot />
      <ModalRoot modals={{ ContractFeeModal }} />
      <ModalRoot modals={{ ContractRuleModal }} />

      <div id="note-component">
        <CustomerNote userName={currentUser.name} pageName={'fee_management'} />
      </div>

      <div className='ledger-title'>
        <h2 className='review-contact-list-header__title'>
          Contract Fee
          <SchoolYearSelect
            onSelect={(year) => onSchoolYearChange(year)}
            schoolYears={dropdownSchoolYears}
            schoolYearId={schoolYearId}
            timeframeFetcher={timeframeFetcher}
            onLockToggle={setIsLocked}
          />
        </h2>
        {contractFees.error && <div className={'alert alert-danger'}>{contractFees.errorMessage}</div>}

        <Filters
          fields={filterFields.concat(additionalFields)}
          count={contractFees.ready ? contractFees.total : null}
          selected={filters}
          submitFilters={setFilters}
        />
      </div>

      {isSuperAdmin && billingCategories.ready && (
        <div>
          <span
            className={`add-item-button open-dialog lockable ${isLocked ? 'locked' : ''}`}
            onClick={() => {
              if (!isLocked) {
                showContractFeeModal({ school_year_id: schoolYearId });
              }
            }}
          >
            <i className='fa fa-plus-circle' />
            Add a new contract fee
          </span>
        </div>
      )}

      <Placeholder.invisible ready={contractFees.ready}>
        {totalPages > 1 && (
          <div>
            <div className='contact-list-react-pagination'>
              <div className='pagination pagination-right'>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  hideFirstAndLastPageLinks
                  onChange={setPage}
                />
              </div>
            </div>
          </div>
        )}
        <ContactList
          columns={columns}
          contacts={contractFees}
          emptyMessage='There are no contract fees.'
          onSort={onSort}
          sort={sort}
          getSubRowContacts={(contact) => contact.charge_rules}
          makeSubTable={makeContractRulesList}
          subTableColumnSpan={columns.length}
          className='contract-fee-table'
        />
      </Placeholder.invisible>

    </div>
  );
};

ContractFeesList.propTypes = {
  /** The list of contract fees * */
  contractFees: PropTypes.arrayOf(PropTypes.object),

  /** The default fields that are available to filter by */
  fields: PropTypes.arrayOf(PropTypes.object),

  /** The filters selected for this page */
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Used to generate a link to sort by a given column */
  onSort: PropTypes.func,

  /** The current page */
  page: PropTypes.number.isRequired,

  /** Sets filters to a given filterset */
  setFilters: PropTypes.func,

  /** Sets page to a given page */
  setPage: PropTypes.func,

  /** The sort selected for this page */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  }),

  /** Hides all modals */
  hideModal: PropTypes.func,
  /** Shows a flash message */
  showFlash: PropTypes.func,

  /** Shows a modal */
  showModal: PropTypes.func,
  /** Adds a transaction */
  addTransaction: PropTypes.func,

  /** add React dispatch */
  dispatch: PropTypes.func,
  activateContractFee: PropTypes.func,
  deactivateContractFee: PropTypes.func,
  reloadContractFees: PropTypes.func,
  removeContractRule: PropTypes.func,
  currentUser: PropTypes.shape({
    is_super_admin: PropTypes.bool
  }),
  timeframeFetcher: PropTypes.objectOf(TimeframeFetcher),
  allFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    display_name: PropTypes.string,
    ctype: PropTypes.string
  }))
};

ContractFeesList.defaultProps = {
  sort: {},
  onSort: () => false,

  showFlash: () => false,
  showModal: () => false,
  hideModal: () => false,
  removeContractRule: () => false,
  currentUser: {
    is_super_admin: false
  },
  timeframeFetcher: (new TimeframeFetcher(apiFetch)),
  allFields: []
};

const mapDucksToProps = ({ location }) => {
  const query = queryString.parse(location.search);
  return {
    currentUser: singleDuck(usersDuck, 'current'),
    customer: singleDuck(customersDuck, 'current'),
    additionalFields: filterDuck(fieldsDuck, { for: 'billing' }),
    allFields: filterDuck(fieldsDuck),
    billingCategories,
    grades,
    schoolYears,
    contractFees: filterDuck(contractFees, query)
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  ...props,
  setFilters: (newFilters) => {
    props.setFilters(newFilters);
    dispatch(contractFees.actions.markDirty());
  },
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  activateContractFee: (id) => dispatch(contractFees.actions.activate(id)),
  deactivateContractFee: (id) => dispatch(contractFees.actions.deactivate(id)),
  reloadContractFees: () => dispatch(contractFees.actions.markDirty()),
  removeContractRule: (contractRule) => dispatch(chargeRules.actions.remove(contractRule))
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({ column: 'name', order: 'desc' }),
  withDateFilter(),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(({ customer }) => customer.current_enrollment_year)
)(ContractFeesList);
